import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Project, ManagerStats } from '@/types';
import apiService from '@/services/api';
import Card from '@/components/ui/Card';
import { Users, FolderKanban, Clock, DollarSign, TrendingUp, ChevronRight } from 'lucide-react';

interface ManagerSectionsProps {
  managedProjects: Project[];
  maxHeight?: number;
}

const ManagerSections: React.FC<ManagerSectionsProps> = ({ 
  managedProjects, 
  maxHeight 
}) => {
  const navigate = useNavigate();
  const [managerStats, setManagerStats] = useState<ManagerStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const containerStyle = maxHeight ? { height: `${maxHeight}px` } : {};

  useEffect(() => {
    fetchManagerStats();
  }, []);

  const fetchManagerStats = async () => {
    try {
      const stats = await apiService.getManagerStats();
      setManagerStats(stats);
    } catch (error: any) {
      setError('获取管理统计数据失败');
      console.error('Failed to fetch manager stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 overflow-y-auto" style={containerStyle}>
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 overflow-y-auto" style={containerStyle}>
        <Card>
          <Card.Content className="p-6 text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={fetchManagerStats}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              重试
            </button>
          </Card.Content>
        </Card>
      </div>
    );
  }

  const StatCard: React.FC<{
    title: string;
    value: string | number;
    icon: React.ReactNode;
    color: string;
    onClick?: () => void;
  }> = ({ title, value, icon, color, onClick }) => (
    <div 
      className={`p-4 rounded-lg border-2 transition-all ${
        onClick ? 'cursor-pointer hover:shadow-md hover:border-opacity-80' : ''
      } ${color}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
        <div className="opacity-80">
          {icon}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6 overflow-y-auto" style={containerStyle}>
      
      {/* 管理统计概览 */}
      <Card>
        <Card.Header>
          <Card.Title className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <TrendingUp className="h-5 w-5 text-purple-600" />
              <span>我的管理统计</span>
            </div>
            <button
              onClick={() => navigate('/projects')}
              className="flex items-center text-sm text-blue-600 hover:text-blue-800"
            >
              项目管理
              <ChevronRight className="h-4 w-4 ml-1" />
            </button>
          </Card.Title>
        </Card.Header>
        <Card.Content>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <StatCard
              title="管理项目"
              value={managerStats?.total_managed_projects || 0}
              icon={<FolderKanban className="h-8 w-8 text-blue-600" />}
              color="border-blue-200 bg-blue-50"
              onClick={() => navigate('/projects')}
            />
            <StatCard
              title="进行中"
              value={managerStats?.active_projects || 0}
              icon={<Clock className="h-8 w-8 text-green-600" />}
              color="border-green-200 bg-green-50"
            />
            <StatCard
              title="已完成"
              value={managerStats?.completed_projects || 0}
              icon={<FolderKanban className="h-8 w-8 text-purple-600" />}
              color="border-purple-200 bg-purple-50"
            />
            <StatCard
              title="团队成员"
              value={managerStats?.total_members || 0}
              icon={<Users className="h-8 w-8 text-orange-600" />}
              color="border-orange-200 bg-orange-50"
            />
            <StatCard
              title="总工时"
              value={`${(managerStats?.total_hours || 0).toFixed(1)}h`}
              icon={<Clock className="h-8 w-8 text-indigo-600" />}
              color="border-indigo-200 bg-indigo-50"
              onClick={() => navigate('/reports')}
            />
            <StatCard
              title="总成本"
              value={`¥${(managerStats?.total_cost || 0).toLocaleString()}`}
              icon={<DollarSign className="h-8 w-8 text-red-600" />}
              color="border-red-200 bg-red-50"
              onClick={() => navigate('/reports')}
            />
          </div>
        </Card.Content>
      </Card>

      {/* 项目详细列表 */}
      <Card>
        <Card.Header>
          <Card.Title className="flex items-center space-x-2">
            <FolderKanban className="h-5 w-5 text-blue-600" />
            <span>项目详情</span>
          </Card.Title>
        </Card.Header>
        <Card.Content>
          {managerStats?.project_details && managerStats.project_details.length > 0 ? (
            <div className="space-y-3">
              {managerStats.project_details.map((project) => (
                <div
                  key={project.id}
                  onClick={() => navigate('/projects')}
                  className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md transition-all cursor-pointer"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-gray-900">{project.name}</h4>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      project.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                      project.status === 'completed' ? 'bg-green-100 text-green-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {project.status === 'in_progress' ? '进行中' : 
                       project.status === 'completed' ? '已完成' : '未开始'}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div className="flex items-center space-x-2">
                      <Users className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-600">成员: {project.member_count}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-600">工时: {project.total_hours.toFixed(1)}h</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <DollarSign className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-600">成本: ¥{project.total_cost.toLocaleString()}</span>
                    </div>
                    {project.budget && (
                      <div className="flex items-center space-x-2">
                        <TrendingUp className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-600">预算: ¥{project.budget.toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                  
                  {project.description && (
                    <p className="mt-3 text-sm text-gray-500 line-clamp-2">
                      {project.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <FolderKanban className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium mb-2">暂无管理的项目</p>
              <p className="text-sm">等待管理员分配项目管理权限</p>
            </div>
          )}
        </Card.Content>
      </Card>
    </div>
  );
};

export default ManagerSections;